import React, { Suspense, lazy, useState, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isAuthenticated, isHealthOnly } from './services/auth';
import { ROUTES } from './utils/constants';
import { LayoutProvider } from './contexts/LayoutContext';
import { HarmonizeProvider } from './contexts/HarmonizeContext';
import { DashboardCountsProvider } from './contexts/DashboardCountsContext';
import NotificationManager from './components/NotificationManager';
import CommandPalette from './components/common/CommandPalette';
import KeyboardShortcutsHelp from './components/common/KeyboardShortcutsHelp';
import ErrorBoundary from './components/common/ErrorBoundary';
import { queryClient } from './lib/queryClient';
import { storage } from './utils/storage';
import { initAuthFailedState } from './services/authFailedState';
import { useWidgetData } from './hooks/useWidgetData';
import { useShareExtension } from './hooks/useShareExtension';

// Loading component with retry for backend warmup
const LoadingFallback = () => {
  const [showRetry, setShowRetry] = React.useState(false);

  // Show retry button after 10 seconds (backend warmup timeout)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowRetry(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center max-w-md">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-lg font-medium text-gray-900 mb-2">Loading...</p>
        <p className="text-sm text-gray-600 mb-6">
          {showRetry
            ? 'This is taking longer than expected. The backend may be warming up.'
            : 'Please wait while we load your workspace.'}
        </p>
        {showRetry && (
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium min-h-[44px]"
          >
            Refresh Page
          </button>
        )}
      </div>
    </div>
  );
};

// Lazy load pages for code splitting
const SignUp = lazy(() => import('./pages/SignUp'));
const LogIn = lazy(() => import('./pages/LogIn'));
const GoogleOAuthCallback = lazy(() => import('./pages/GoogleOAuthCallback'));
const MicrosoftCallback = lazy(() => import('./pages/MicrosoftCallback'));
const WhoopCallback = lazy(() => import('./pages/WhoopCallback'));
const Settings = lazy(() => import('./pages/Settings'));
const Focus = lazy(() => import('./pages/Focus'));
const Today = lazy(() => import('./pages/Today'));
const InboxNew = lazy(() => import('./pages/InboxNew'));
const Betabox = lazy(() => import('./pages/Betabox'));
const EmailThread = lazy(() => import('./pages/EmailThread'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Tasks = lazy(() => import('./pages/Tasks'));
const TaskInsights = lazy(() => import('./pages/TaskInsights'));
const TaskAnalytics = lazy(() => import('./pages/TaskAnalytics'));
const NotificationDetail = lazy(() => import('./pages/NotificationDetail'));
const Contacts = lazy(() => import('./pages/Contacts'));
const ContactDetail = lazy(() => import('./pages/ContactDetail'));
const ContactsHarmonize = lazy(() => import('./pages/ContactsHarmonize'));
const ContactsHarmonizeCircles = lazy(() => import('./pages/ContactsHarmonizeCircles'));
const ContactsHarmonizeMerge = lazy(() => import('./pages/ContactsHarmonizeMerge'));
const Goals = lazy(() => import('./pages/Goals'));
const GoalDetail = lazy(() => import('./pages/GoalDetail'));
const Endeavors = lazy(() => import('./pages/EndeavorCanvas'));
const EndeavorDetail = lazy(() => import('./pages/EndeavorDetail'));
const EndeavorWorkspace = lazy(() => import('./pages/EndeavorWorkspace'));
const WeeklyReviews = lazy(() => import('./pages/WeeklyReviews'));
const Notes = lazy(() => import('./pages/Notes'));
const Bookmarks = lazy(() => import('./pages/Bookmarks'));
const Documents = lazy(() => import('./pages/Documents'));
const AffinityDiagrams = lazy(() => import('./pages/AffinityDiagrams'));
const AffinityDiagramEditor = lazy(() => import('./pages/AffinityDiagramEditor'));
const AffinityDndSandbox = lazy(() => import('./pages/AffinityDndSandbox'));
const Health = lazy(() => import('./pages/Health'));
const HealthMetricDetail = lazy(() => import('./pages/HealthMetricDetail'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminErrorLogs = lazy(() => import('./pages/AdminErrorLogs'));
const AdminQualityReport = lazy(() => import('./pages/AdminQualityReport'));
const PrepPackDetail = lazy(() => import('./pages/PrepPackDetail'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Budget = lazy(() => import('./pages/Budget'));
const Burn = lazy(() => import('./pages/Burn'));
const Allocations = lazy(() => import('./pages/Allocations'));
const Loans = lazy(() => import('./pages/Loans'));
const FinanceCalendar = lazy(() => import('./pages/FinanceCalendar'));
const Merchants = lazy(() => import('./pages/Merchants'));
const Subscriptions = lazy(() => import('./pages/Subscriptions'));
const AngelInvesting = lazy(() => import('./pages/AngelInvesting'));
const CompanyDetail = lazy(() => import('./pages/CompanyDetail'));
const Chat = lazy(() => import('./pages/Chat'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// Protected Route wrapper
interface ProtectedRouteProps {
  children: React.ReactElement;
}

// Where an authenticated user should land: health_only accounts go to Health.
const landingRoute = (): string => (isHealthOnly() ? ROUTES.HEALTH : ROUTES.INBOX);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  // health_only accounts are confined to Health (+ their own settings). The
  // backend guard is authoritative; this keeps the UI from showing dead ends.
  if (isHealthOnly()) {
    const p = location.pathname;
    if (!(p.startsWith('/health') || p.startsWith('/settings'))) {
      return <Navigate to={ROUTES.HEALTH} replace />;
    }
  }
  return children;
};

// Public Route wrapper (redirect to landing if already authenticated)
const PublicRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  if (isAuthenticated()) {
    return <Navigate to={landingRoute()} replace />;
  }
  return children;
};

const AppInner: React.FC = () => {
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
  const [storageInitialized, setStorageInitialized] = useState(false);

  // Push calendar/task data to iOS widget shared storage (no-op on web)
  useWidgetData();

  // Auto-provision API key for iOS Share Extension (no-op on web)
  useShareExtension();

  // Initialize storage on app start (syncs Capacitor Preferences to localStorage on iOS)
  // This ensures returning users have their auth data available for synchronous isAuthenticated() checks
  useEffect(() => {
    Promise.all([
      storage.initialize(),
      initAuthFailedState(),
    ]).then(() => {
      setStorageInitialized(true);
    }).catch((err) => {
      console.error('Storage initialization failed:', err);
      setStorageInitialized(true); // Continue anyway
    });
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = e.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(true);
        return;
      }

      // ? for keyboard shortcuts help (only when not in input field)
      if (e.key === '?' && !isInputField && e.shiftKey) {
        e.preventDefault();
        setIsShortcutsHelpOpen(true);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle app lifecycle for iOS
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupListener = async () => {
      listenerHandle = await CapacitorApp.addListener('appStateChange', async ({ isActive }: { isActive: boolean }) => {
        if (isActive) {
          // CRITICAL: Skip auth verification when on the login page.
          // On iOS, appStateChange can fire while the user is typing on the login form.
          // Calling verifyAuthState() with stale credentials triggers a 401 → refresh fail
          // → redirectToLogin() → hard page reload → form input lost.
          if (window.location.pathname === '/login' || window.location.pathname === '/signup') {
            return;
          }

          // Import dynamically to avoid circular dependencies
          const { verifyAuthState, isAuthenticated: checkAuth } = await import('./services/auth');

          const isValid = await verifyAuthState();

          if (!isValid && window.location.pathname !== '/login') {
            // Auth invalid on foreground - the API interceptor should have handled redirect
          }
        }
      });
    };

    setupListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, []);

  // Wait for storage initialization before rendering routes
  // This ensures isAuthenticated() has access to synced data from Capacitor Preferences
  if (!storageInitialized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-8 w-8 text-primary-600"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
    );
  }

  return (
        <LayoutProvider>
          <DashboardCountsProvider>
          <HarmonizeProvider>
            <NotificationManager />

          {/* Command Palette */}
          <CommandPalette
            isOpen={isCommandPaletteOpen}
            onClose={() => setIsCommandPaletteOpen(false)}
          />

          {/* Keyboard Shortcuts Help */}
          <KeyboardShortcutsHelp
            isOpen={isShortcutsHelpOpen}
            onClose={() => setIsShortcutsHelpOpen(false)}
          />

          <Toaster
          position="top-center"
          containerStyle={{
            top: 60, // Add padding to avoid iOS notch/Dynamic Island
          }}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        {/* Error Boundary to catch runtime errors and prevent white screen */}
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
        {/* Root - redirect to login or inbox */}
        <Route
          path={ROUTES.HOME}
          element={
            isAuthenticated() ? (
              <Navigate to={landingRoute()} replace />
            ) : (
              <Navigate to={ROUTES.LOGIN} replace />
            )
          }
        />

        {/* Public routes */}
        <Route
          path={ROUTES.SIGNUP}
          element={
            <PublicRoute>
              <SignUp />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <LogIn />
            </PublicRoute>
          }
        />

        {/* OAuth callback - no protection needed */}
        <Route path={ROUTES.GOOGLE_CALLBACK} element={<GoogleOAuthCallback />} />
        <Route path={ROUTES.MICROSOFT_CALLBACK} element={<MicrosoftCallback />} />
        <Route path={ROUTES.WHOOP_CALLBACK} element={<WhoopCallback />} />

        {/* Legal pages - public, no auth wrapper */}
        <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
        <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />

        {/* Dashboard route - redirect to the user's landing page */}
        <Route
          path={ROUTES.DASHBOARD}
          element={<Navigate to={landingRoute()} replace />}
        />

        {/* Protected routes */}
        <Route
          path={ROUTES.SETTINGS}
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.FOCUS}
          element={
            <ProtectedRoute>
              <Focus />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TODAY}
          element={
            <ProtectedRoute>
              <Today />
            </ProtectedRoute>
          }
        />
        <Route
          path="/prep-packs/:id"
          element={
            <ProtectedRoute>
              <PrepPackDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.INBOX}
          element={
            <ProtectedRoute>
              <InboxNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/inbox/:emailId"
          element={
            <ProtectedRoute>
              <InboxNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/betabox"
          element={
            <ProtectedRoute>
              <Betabox />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.EMAIL_THREAD}
          element={
            <ProtectedRoute>
              <EmailThread />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CALENDAR}
          element={
            <ProtectedRoute>
              <Calendar />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TASKS}
          element={
            <ProtectedRoute>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tasks/insights"
          element={
            <ProtectedRoute>
              <TaskInsights />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TASK_ANALYTICS}
          element={
            <ProtectedRoute>
              <TaskAnalytics />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications/:id"
          element={
            <ProtectedRoute>
              <NotificationDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACTS}
          element={
            <ProtectedRoute>
              <Contacts />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACT_DETAIL}
          element={
            <ProtectedRoute>
              <ContactDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACTS_HARMONIZE}
          element={
            <ProtectedRoute>
              <ContactsHarmonize />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACTS_HARMONIZE_CIRCLES}
          element={
            <ProtectedRoute>
              <ContactsHarmonizeCircles />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACTS_HARMONIZE_MERGE}
          element={
            <ProtectedRoute>
              <ContactsHarmonizeMerge />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.GOALS}
          element={
            <ProtectedRoute>
              <Goals />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.GOAL_DETAIL}
          element={
            <ProtectedRoute>
              <GoalDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ENDEAVORS}
          element={
            <ProtectedRoute>
              <Endeavors />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ENDEAVOR_DETAIL}
          element={
            <ProtectedRoute>
              <EndeavorDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ENDEAVOR_WORKSPACE}
          element={
            <ProtectedRoute>
              <EndeavorWorkspace />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.WEEKLY_REVIEWS}
          element={
            <ProtectedRoute>
              <WeeklyReviews />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.NOTES}
          element={
            <ProtectedRoute>
              <Notes />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BOOKMARKS}
          element={
            <ProtectedRoute>
              <Bookmarks />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.DOCUMENTS}
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.AFFINITY_DIAGRAMS}
          element={
            <ProtectedRoute>
              <AffinityDiagrams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affinity-diagrams/:id"
          element={
            <ProtectedRoute>
              <AffinityDiagramEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dev/affinity-dnd"
          element={
            <ProtectedRoute>
              <AffinityDndSandbox />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.HEALTH}
          element={
            <ProtectedRoute>
              <Health />
            </ProtectedRoute>
          }
        />
        <Route
          path="/health/:id"
          element={
            <ProtectedRoute>
              <HealthMetricDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CHAT}
          element={
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions"
          element={
            <ProtectedRoute>
              <Transactions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/budget"
          element={
            <ProtectedRoute>
              <Budget />
            </ProtectedRoute>
          }
        />
        <Route
          path="/burn"
          element={
            <ProtectedRoute>
              <Burn />
            </ProtectedRoute>
          }
        />
        <Route
          path="/allocations"
          element={
            <ProtectedRoute>
              <Allocations />
            </ProtectedRoute>
          }
        />
        <Route
          path="/investments"
          element={<Navigate to="/angel-investing" replace />}
        />
        <Route
          path="/loans"
          element={
            <ProtectedRoute>
              <Loans />
            </ProtectedRoute>
          }
        />
        <Route
          path="/angel-investing"
          element={
            <ProtectedRoute>
              <AngelInvesting />
            </ProtectedRoute>
          }
        />
        <Route
          path="/angel-investing/companies/:id"
          element={
            <ProtectedRoute>
              <CompanyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance-calendar"
          element={
            <ProtectedRoute>
              <FinanceCalendar />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merchants"
          element={
            <ProtectedRoute>
              <Merchants />
            </ProtectedRoute>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <ProtectedRoute>
              <Subscriptions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />
	        <Route
	          path="/admin/error-logs"
	          element={
	            <ProtectedRoute>
	              <AdminErrorLogs />
	            </ProtectedRoute>
	          }
	        />
	        <Route
	          path="/admin/quality"
	          element={
	            <ProtectedRoute>
	              <AdminQualityReport />
	            </ProtectedRoute>
	          }
	        />

        {/* 404 - redirect to home */}
        <Route path="*" element={<Navigate to={ROUTES.HOME} replace />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        </HarmonizeProvider>
          </DashboardCountsProvider>
      </LayoutProvider>
  );
};

// Data router (single catch-all route) so useBlocker works (e.g. Betabox's
// unsaved-triage guard); the descendant <Routes> in AppInner are unaffected.
const router = createBrowserRouter([{ path: '*', element: <AppInner /> }]);

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>
);

export default App;
